import React, {
	createContext,
	useCallback,
	useContext,
	useEffect,
	useRef,
	type ReactNode,
} from 'react';

import { getDocument } from '@atlaskit/browser-apis';

import { useAnalyticsEvents } from '../generated/use-analytics-events';

/**
 * The closed set of actions that count as a user's first meaningful use of
 * the Labs workbench. Scrolling, hovering, and clicks on inert page chrome
 * are deliberately absent: counting those would report almost every visit as
 * engaged and make the interaction rate meaningless.
 */
export const LabsFirstInteractionType = {
	/** A predefined Cypher starter was loaded into the editor. */
	StarterQuerySelected: 'starterQuerySelected',
	/** The user changed Cypher or GraphQL editor content. */
	EditorEdited: 'editorEdited',
	/** A query was run. */
	QueryExecuted: 'queryExecuted',
	/** The user switched between the Cypher and GraphQL editor tabs. */
	EditorTabSwitched: 'editorTabSwitched',
	/** The settings tab was toggled. */
	SettingsToggled: 'settingsToggled',
	/** The Cyphie AI chat tab was toggled. */
	AiChatToggled: 'aiChatToggled',
	/** The editor contents were formatted. */
	QueryFormatted: 'queryFormatted',
	/** A shareable link to the current query was copied. */
	ShareableLinkCopied: 'shareableLinkCopied',
	SavedQuerySelected: 'savedQuerySelected',
	SavedQueryTested: 'savedQueryTested',
	SaveQueryOpened: 'saveQueryOpened',
} as const;

export type LabsFirstInteraction =
	(typeof LabsFirstInteractionType)[keyof typeof LabsFirstInteractionType];

/**
 * The closed set of error states Labs shows. The raw message is never sent:
 * query text, results, and request identifiers stay off the event.
 */
export const LabsErrorType = {
	/** Cypher rejected the query as a semantic error. */
	Semantic: 'semantic',
	/** The query had no usable site or graph context. */
	SiteContext: 'siteContext',
	/** The gateway asked the client to slow down. */
	RateLimit: 'rateLimit',
	/** The query timed out or was aborted. */
	Timeout: 'timeout',
	/** The underlying service reported a generic failure. */
	Service: 'service',
	/** Any other query or network error that Labs showed. */
	Query: 'query',
	/** The UGS schema failed to load. */
	Schema: 'schema',
} as const;

export type LabsError = (typeof LabsErrorType)[keyof typeof LabsErrorType];

/**
 * Report an error state Labs is showing. When a failure object is supplied,
 * that object is recorded once per visit.
 */
export type ReportLabsError = (errorType: LabsError, failure?: object) => void;

/**
 * The user edited the Cypher editor after a query failure. Only the first
 * edit after each failure is a reattempt; later keystrokes are not.
 */
export type ReportLabsCypherReattempt = () => void;

/**
 * A query result was shown with no error, so an earlier failure is no longer
 * unresolved.
 */
export type ReportLabsQuerySuccess = (result?: object) => void;

/**
 * A query was run from the Labs editor. An earlier run that never had a
 * result click is closed first.
 */
export type ReportLabsQueryRun = () => void;

/**
 * The user clicked a results row or a smart link in the results, so the open
 * query run is no longer unclicked.
 */
export type ReportLabsResultEngaged = () => void;

/** The paperclip was clicked to copy a shareable Labs link. */
export type ReportLabsLinkCopied = () => void;

/**
 * A click in the results. `smartLink` is a result rendered as a smart link.
 * `row` is a click on the results row itself. A smart link click is not also
 * a row click.
 */
export const LabsResultClickTarget = {
	SmartLink: 'smartLink',
	Row: 'row',
} as const;

export type LabsResultClick = (typeof LabsResultClickTarget)[keyof typeof LabsResultClickTarget];

export type ReportLabsResultClick = (clickTarget: LabsResultClick) => void;

/**
 * The results view the user is looking at. `table` is the row view.
 */
export const LabsResultsView = {
	Table: 'table',
	Json: 'json',
} as const;

export type LabsResultsViewMode = (typeof LabsResultsView)[keyof typeof LabsResultsView];

/**
 * Report the results view on screen. The same result in the same view is
 * recorded once per visit, so a second pane does not count again. Switching
 * to the other view records that view.
 */
export type ReportLabsResultsView = (view: LabsResultsViewMode, result?: object) => void;

/**
 * A helper button in the results box. `splunkLogs` opens Splunk, `queryHelp`
 * is Get help with query, `queryPerformance` is View query performance, and
 * `downloadJson` is Download as JSON.
 */
export const LabsHelperButton = {
	SplunkLogs: 'splunkLogs',
	QueryHelp: 'queryHelp',
	QueryPerformance: 'queryPerformance',
	DownloadJson: 'downloadJson',
} as const;

export type LabsHelper = (typeof LabsHelperButton)[keyof typeof LabsHelperButton];

/** One click of a results-box helper. Each click fires once. */
export type ReportLabsHelperClick = (helper: LabsHelper) => void;

/**
 * A Cyphie response feedback control. `thumbsUp` and `thumbsDown` are the
 * rating buttons. The other values are the reasons shown after thumbs down.
 */
export const LabsCyphieFeedback = {
	ThumbsUp: 'thumbsUp',
	ThumbsDown: 'thumbsDown',
	QueryDoesNotRun: 'queryDoesNotRun',
	WrongResults: 'wrongResults',
	DidNotFollowAsk: 'didNotFollowAsk',
	Other: 'other',
} as const;

export type LabsCyphieFeedbackTarget = (typeof LabsCyphieFeedback)[keyof typeof LabsCyphieFeedback];

/** One click of a Cyphie feedback control. Each click fires once. */
export type ReportLabsCyphieFeedback = (feedback: LabsCyphieFeedbackTarget) => void;

/**
 * Cyphie mode for a sent message. `fast` is the Default mode in Labs.
 * `reasoning` is Reasoning mode.
 */
export const LabsCyphieMode = {
	Fast: 'fast',
	Reasoning: 'reasoning',
} as const;

export type LabsCyphieModeChoice = (typeof LabsCyphieMode)[keyof typeof LabsCyphieMode];

/** One message sent to Cyphie. The first message in a visit also records that Cyphie was used. */
export type ReportLabsCyphieMessage = (mode: LabsCyphieModeChoice) => void;

/** One Use in Editor click on a query Cyphie returned. The next query run is its first try. */
export type ReportLabsCyphieQuerySelected = () => void;

/**
 * The Labs color mode on screen. `dark` includes a theme choice of auto, which
 * Labs stores as dark.
 */
export const LabsColorModeChoice = {
	Light: 'light',
	Dark: 'dark',
} as const;

export type LabsAppearanceMode = (typeof LabsColorModeChoice)[keyof typeof LabsColorModeChoice];

/**
 * The color mode Labs is showing. The same mode in a row is recorded once per
 * visit. Switching back to an earlier mode records it again.
 */
export type ReportLabsColorMode = (colorMode: LabsAppearanceMode) => void;

/**
 * A link under Want even more info. `cypherQueries` is the Cypher writing
 * guide, `buildTwg` is building teamwork graph into an app, `roadmap` is the
 * roadmap, and `shareFeedback` is the feedback form.
 */
export const LabsMoreInfoLink = {
	CypherQueries: 'cypherQueries',
	BuildTwg: 'buildTwg',
	Roadmap: 'roadmap',
	ShareFeedback: 'shareFeedback',
} as const;

export type LabsMoreInfo = (typeof LabsMoreInfoLink)[keyof typeof LabsMoreInfoLink];

/** One click of a more-info link. Each click fires once. */
export type ReportLabsMoreInfoClick = (link: LabsMoreInfo) => void;

/** Whether the first run of a selected Cyphie query succeeded. */
export const LabsCyphieQueryFirstRun = {
	Success: 'success',
	Error: 'error',
} as const;

export type LabsCyphieQueryFirstRunOutcome =
	(typeof LabsCyphieQueryFirstRun)[keyof typeof LabsCyphieQueryFirstRun];

/**
 * A successful Cypher result with no rows. The same result is recorded once
 * per visit.
 */
export type ReportLabsEmptyResult = (result: object) => void;

/** Reads the current Labs visit id. Empty when the tracker is not mounted. */
export type ReadLabsVisitId = () => string;

const noopReportLabsError: ReportLabsError = () => {};
const noopReportLabsCypherReattempt: ReportLabsCypherReattempt = () => {};
const noopReportLabsQuerySuccess: ReportLabsQuerySuccess = () => {};
const noopReportLabsQueryRun: ReportLabsQueryRun = () => {};
const noopReportLabsResultEngaged: ReportLabsResultEngaged = () => {};
const noopReportLabsLinkCopied: ReportLabsLinkCopied = () => {};
const noopReportLabsResultClick: ReportLabsResultClick = () => {};
const noopReportLabsResultsView: ReportLabsResultsView = () => {};
const noopReportLabsHelperClick: ReportLabsHelperClick = () => {};
const noopReportLabsCyphieFeedback: ReportLabsCyphieFeedback = () => {};
const noopReportLabsCyphieMessage: ReportLabsCyphieMessage = () => {};
const noopReportLabsCyphieQuerySelected: ReportLabsCyphieQuerySelected = () => {};

/**
 * Cyphie lives in the app aside, which renders beside the page rather than
 * under `LabsSessionTracker`. Context from the tracker does not reach that
 * panel, so the tracker publishes its reporters here and the Cyphie hooks
 * read them when they are outside the provider. Cleared on unmount.
 */
const labsCyphiePortal: {
	feedback: ReportLabsCyphieFeedback;
	message: ReportLabsCyphieMessage;
	querySelected: ReportLabsCyphieQuerySelected;
} = {
	feedback: noopReportLabsCyphieFeedback,
	message: noopReportLabsCyphieMessage,
	querySelected: noopReportLabsCyphieQuerySelected,
};
const noopReportLabsColorMode: ReportLabsColorMode = () => {};
const noopReportLabsMoreInfoClick: ReportLabsMoreInfoClick = () => {};
const noopReportLabsEmptyResult: ReportLabsEmptyResult = () => {};
const noopReadLabsVisitId: ReadLabsVisitId = () => '';

const LabsErrorContext = createContext<ReportLabsError>(noopReportLabsError);
const LabsCypherReattemptContext = createContext<ReportLabsCypherReattempt>(
	noopReportLabsCypherReattempt,
);
const LabsQuerySuccessContext = createContext<ReportLabsQuerySuccess>(noopReportLabsQuerySuccess);
const LabsQueryRunContext = createContext<ReportLabsQueryRun>(noopReportLabsQueryRun);
const LabsResultEngagedContext = createContext<ReportLabsResultEngaged>(
	noopReportLabsResultEngaged,
);
const LabsLinkCopiedContext = createContext<ReportLabsLinkCopied>(noopReportLabsLinkCopied);
const LabsResultClickContext = createContext<ReportLabsResultClick>(noopReportLabsResultClick);
const LabsResultsViewContext = createContext<ReportLabsResultsView>(noopReportLabsResultsView);
const LabsHelperClickContext = createContext<ReportLabsHelperClick>(noopReportLabsHelperClick);
const LabsCyphieFeedbackContext = createContext<ReportLabsCyphieFeedback>(
	noopReportLabsCyphieFeedback,
);
const LabsCyphieMessageContext = createContext<ReportLabsCyphieMessage>(
	noopReportLabsCyphieMessage,
);
const LabsCyphieQuerySelectedContext = createContext<ReportLabsCyphieQuerySelected>(
	noopReportLabsCyphieQuerySelected,
);
const LabsColorModeContext = createContext<ReportLabsColorMode>(noopReportLabsColorMode);
const LabsMoreInfoClickContext = createContext<ReportLabsMoreInfoClick>(
	noopReportLabsMoreInfoClick,
);
const LabsEmptyResultContext = createContext<ReportLabsEmptyResult>(noopReportLabsEmptyResult);
const LabsVisitIdContext = createContext<ReadLabsVisitId>(noopReadLabsVisitId);

/**
 * Report a meaningful interaction with the Labs workbench. Safe to call on
 * every interaction: only the first one in a visit produces an event.
 */
export type ReportLabsFirstInteraction = (interactionType: LabsFirstInteraction) => void;

/**
 * Why a visit was abandoned. `pageHidden` is a backgrounded or closed tab;
 * `unmounted` is in-app navigation away from Labs.
 */
type LabsAbandonReason = 'pageHidden' | 'unmounted';

const noopReportLabsFirstInteraction: ReportLabsFirstInteraction = () => {};

const LabsFirstInteractionContext = createContext<ReportLabsFirstInteraction>(
	noopReportLabsFirstInteraction,
);

/**
 * Returns a monotonic clock reading in milliseconds. Falls back to
 * `Date.now()` in environments where `performance.now()` is unavailable
 * (e.g. some SSR shims). `performance.now()` is preferred because it is
 * not affected by wall-clock skew (NTP corrections, manual clock changes)
 * and therefore cannot produce negative `durationMs` values that would
 * pollute the time-on-page / average session duration dashboards.
 */
const now = (): number =>
	typeof performance !== 'undefined' && typeof performance.now === 'function'
		? performance.now()
		: Date.now();

/**
 * Opaque identifier used only to stitch a page view together with the
 * duration segments belonging to the same visit. Deliberately random and
 * per-visit: it is not derived from, and cannot be joined back to, a user,
 * tenant, or any object in the graph.
 *
 * `crypto.randomUUID` requires a secure context, so a non-cryptographic
 * fallback keeps the tracker working (rather than throwing and taking the
 * page down) on plain-HTTP origins and older runtimes.
 */
const createVisitId = (): string =>
	typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
		? crypto.randomUUID()
		: `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`;

/**
 * The paperclip writes the tenant and the request headers onto the page URL.
 * Those headers carry the site context, including the `X-Query-Context` ARI.
 * Query Builder links carry tenant and Cypher parameters without that header.
 * A visit that arrives with both loaded Labs from that link. The values stay
 * off the event.
 */
const openedFromShareableLink = (surface: 'twgLabs' | 'queryBuilder'): boolean => {
	const href = getDocument()?.defaultView?.location.href;
	if (!href) {
		return false;
	}
	try {
		const searchParams = new URL(href).searchParams;
		if (surface === 'queryBuilder') {
			return Boolean(searchParams.get('tenant')?.trim() && searchParams.get('cypher')?.trim());
		}
		const tenant = searchParams.get('tenant')?.trim() ?? '';
		const headersRaw = searchParams.get('headers')?.trim() ?? '';
		if (!tenant || !headersRaw) {
			return false;
		}
		const parsed = JSON.parse(headersRaw) as unknown;
		if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
			return false;
		}
		const queryContext = Object.entries(parsed as Record<string, unknown>).find(
			([key]) => key.toLowerCase() === 'x-query-context',
		)?.[1];
		return typeof queryContext === 'string' && queryContext.trim().length > 0;
	} catch {
		return false;
	}
};

/**
 * Shared visit tracker for Labs and Query Builder. The surface is fixed for
 * each mount. Query Builder supplies its own event names while reusing the
 * existing workbench callbacks and visit counting rules.
 *
 * Time on page is measured as a series of *visible segments* rather than as a
 * single span, so that a visit which is backgrounded and returned to is
 * measured in full. A user who spends 1 minute on Labs, switches tabs for an
 * hour, then returns for 9 minutes emits two `session.ended` events of 1 and 9
 * minutes under one `visitId` — 10 minutes of attention, with the hour in the
 * other tab excluded. Each event carries only the time since the page last
 * became visible, so segments can be summed without double counting.
 *
 * Fires:
 *  - `screen.twgLabs.viewed` once per *visit*: on mount, and again on a
 *    back/forward-cache restore, which is treated as a new visit and issued a
 *    fresh `visitId`. Emitting the page view alongside the new `visitId` keeps
 *    visit counts and duration records in step; without it a restore would
 *    produce two sets of duration records but one page view, leaving consumers
 *    unable to tell one visit from two when computing time per visit.
 *  - `track.session.ended.twgLabs` on every transition to hidden
 *    (`visibilitychange` to `'hidden'` OR `pagehide`), carrying `durationMs`
 *    for that segment and the `visitId` it belongs to.
 *  - `track.twgLabs.firstInteracted` the first time a descendant reports
 *    a meaningful interaction, carrying that same `visitId` and the visible
 *    time elapsed since the visit started. At most once per visit; a
 *    back/forward-cache restore starts a new visit and arms it again.
 *  - `track.twgLabs.abandoned` the first time the visit goes away before any
 *    such interaction: the page becoming hidden, or the Labs route unmounting.
 *    At most once per visit, on the same visible-time clock. A later
 *    interaction in that visit still reports firstInteracted; abandon is not
 *    retracted. A back/forward-cache restore arms it again under the new visit.
 *  - `track.twgLabs.errored` when a descendant reports an error state Labs is
 *    showing, carrying that same `visitId` and which kind of failure it was.
 *    A given failure is recorded once per visit, so another results view or a
 *    remount does not fire again. A later, different failure fires again.
 *  - `track.twgLabs.reattempted` the first time the Cypher editor is edited
 *    after a query failure. A later failure can be reattempted again. Count
 *    these for how often users rewrite a Cypher query after failure, and
 *    average `durationSinceFailureMs` for the time from the failure until
 *    they start writing. A schema failure does not arm this.
 *  - `track.twgLabs.leftWithoutSuccess` the first time the visit goes away
 *    while a query failure still has no successful result after it. At most
 *    once per visit. Average `durationSinceFailureMs` for the time from that
 *    failure until the user left. A success before leaving does not fire this.
 *  - `track.twgLabs.queryExecuted` each time a nonempty query is run. The
 *    Execute button and the editor shortcut each fire once. An empty editor
 *    does not count. Count events for how many queries were run.
 *  - `track.twgLabs.queryUnclicked` when a query run ends with no result click
 *    since that run. The next query, leaving the page, or a back/forward-cache
 *    restore closes it. Hiding the tab does not, so coming back and clicking
 *    a result still counts. One event per unclicked run.
 *  - `track.twgLabs.linkCopied` each time the paperclip is clicked. Count
 *    events for how often the icon is used, and distinct `visitId`s for how
 *    many visits shared a link.
 *  - `track.twgLabs.linkOpened` when a visit starts from a shareable link.
 *    On page load the URL has a tenant and a headers payload with
 *    `X-Query-Context` set, which is the site ARI the paperclip writes.
 *    At most once per visit, next to the page view. The URL, tenant, and
 *    header values stay off the event.
 *  - `track.twgLabs.resultClicked` each time a descendant reports a smart-link
 *    click or a results-row click. Each click fires once. A smart link click
 *    is not also counted as a row click.
 *  - `track.twgLabs.resultsViewUsed` when results are shown in the row view
 *    (`table`) or the JSON view, and again when the user switches view. The
 *    same result in the same view is recorded once per visit.
 *  - `track.twgLabs.helperClicked` each time a results-box helper is clicked:
 *    See Splunk Logs, Get help with query, View query performance, or Download
 *    as JSON. Each click fires once. Count events for how often any helper is
 *    used, and read `helper` for which one.
 *  - `track.twgLabs.cyphieFeedbackClicked` each time a Cyphie thumbs-up,
 *    thumbs-down, or thumbs-down reason button is clicked. Each click fires
 *    once. Count `thumbsUp` and `thumbsDown` for the rating buttons, and read
 *    the other `feedback` values for which reason was chosen.
 *  - `track.twgLabs.cyphieUsed` the first time a user sends Cyphie a message
 *    in the visit. At most once. Count distinct `visitId`s for visits that
 *    used Cyphie. Opening the panel does not count.
 *  - `track.twgLabs.cyphieMessaged` each time a user sends Cyphie a message.
 *    Each send fires once. Count events for how many messages were sent, and
 *    read `mode` for Fast (`fast`) versus Reasoning (`reasoning`).
 *  - `track.twgLabs.cyphieQuerySelected` each time Use in Editor is clicked on
 *    a query Cyphie returned. The next query run is that query's first try.
 *  - `track.twgLabs.cyphieQueryFirstRun` when that selected query is run for
 *    the first time. `outcome` is `success` or `error`. A schema failure is
 *    not this run. Count `success` against these events for how often a
 *    Cyphie query does not error on the first try.
 *  - `track.twgLabs.colorModeUsed` when Labs is showing light or dark mode,
 *    and again when the user switches. The same mode in a row is recorded
 *    once. A back/forward-cache restore starts a new visit and records the
 *    mode still on screen, because the color-mode effect does not rerun.
 *    The last event for a visit is the mode that visit finished in. Count
 *    distinct `visitId`s by that last `colorMode` for light versus dark.
 *  - `track.twgLabs.moreInfoClicked` each time a link under Want even more
 *    info is clicked. Each click fires once. Count distinct `visitId`s for the
 *    share of visits that used any of those links, and read `link` for which
 *    one is most popular. Query Builder is not included.
 *  - `ui.button.clicked.labsStarterQuery` (Data Portal 110786) each time a
 *    predefined Cypher starter is loaded. `starterKey` says which one. Count
 *    by starterKey for which starters are most popular, and divide distinct
 *    `visitId`s by distinct `visitId`s on `screen.twgLabs.viewed` for the
 *    share of visits that use a starter. The visit id is attached here.
 *  - `track.twgLabs.emptyResult` when a Cypher query succeeds and returns no
 *    rows. The same result is recorded once per visit. Count these for how
 *    many successful queries return 0 results. A query error is not included.
 *
 * Descendants report interactions through {@link useReportLabsFirstInteraction},
 * error states through {@link useReportLabsError}, Cypher edits after a failure
 * through {@link useReportLabsCypherReattempt}, a result with no error through
 * {@link useReportLabsQuerySuccess}, query runs through
 * {@link useReportLabsQueryRun}, result clicks that clear an unclicked query
 * through {@link useReportLabsResultEngaged}, paperclip clicks through
 * {@link useReportLabsLinkCopied}, result clicks through
 * {@link useReportLabsResultClick}, the results view through
 * {@link useReportLabsResultsView}, results-box helpers through
 * {@link useReportLabsHelperClick}, Cyphie response feedback through
 * {@link useReportLabsCyphieFeedback}, Cyphie messages through
 * {@link useReportLabsCyphieMessage}, Use in Editor through
 * {@link useReportLabsCyphieQuerySelected}, the color mode on screen through
 * {@link useTrackLabsColorMode}, more-info links through
 * {@link useReportLabsMoreInfoClick}, the current visit id through
 * {@link useLabsVisitId}, and empty Cypher results through
 * {@link useReportLabsEmptyResult}.
 * The hooks are no-ops when this tracker is not mounted, so workbench controls
 * still render in tests and Storybook.
 *
 * Switching browser tabs stays within one visit (same `visitId`, no extra page
 * view); a back/forward-cache restore starts a new one.
 *
 * The events are content-free by design: they carry no query text, result
 * values, URLs, or object identifiers, in line with the Labs analytics privacy
 * boundaries.
 */
export const LabsSessionTracker = ({
	children,
	surface = 'twgLabs',
}: {
	children?: ReactNode;
	surface?: 'twgLabs' | 'queryBuilder';
}): JSX.Element => {
	const surfaceRef = useRef(surface);
	const queryStartedAt = useRef<number | null>(null);
	const { fireEvent } = useAnalyticsEvents();
	// `useAnalyticsEvents` memoises `fireEvent` with
	// `useCallback(..., [createAnalyticsEvent])`, so its identity changes
	// whenever `createAnalyticsEvent` does. The listeners below are bound once
	// per mount and outlive those changes, so they must read the current value
	// through a ref rather than close over one instance: otherwise a
	// post-mount identity change would leave every subsequent segment and
	// restore event firing through a stale client. Kept in sync from an effect
	// (rather than assigned during render) so render stays side-effect free.
	const fireEventRef = useRef(fireEvent);
	useEffect(() => {
		fireEventRef.current = fireEvent;
	});

	const visitId = useRef(createVisitId());
	// Start of the segment currently being measured, and whether one is open.
	// A segment is open whenever the page is visible and has not yet reported
	// the time accrued since it became so.
	const segmentStart = useRef(now());
	const segmentOpen = useRef(true);
	// Sum of the visible segments already closed in this visit. Added to the
	// open segment to answer "how long had the user been looking when they
	// first interacted?", on the same clock as `durationMs`.
	const accruedMs = useRef(0);
	const firstInteractionFired = useRef(false);
	// The first Cyphie message in this visit. A back/forward-cache restore arms it again.
	const cyphieUsedFired = useRef(false);
	// True after Use in Editor until that query's next non-schema result.
	const cyphieQueryAwaitingFirstRun = useRef(false);
	// The color mode already recorded for the current stretch. A repeat of the
	// same mode does not count again. A switch, including a switch back, does.
	// A back/forward-cache restore keeps this value and records it again under
	// the new visitId. The color-mode effect does not rerun on that restore.
	const lastReportedColorMode = useRef<LabsAppearanceMode | null>(null);
	// At most one abandon per visit. Distinct from firstInteractionFired: hiding
	// the tab before any interaction abandons the visit, and a later interaction
	// after the user returns is still reported.
	const abandonedFired = useRef(false);
	// Distinguishes a real unmount from StrictMode's effect cleanup → re-run.
	// Firing abandon synchronously in that cleanup would record a bounce for
	// every development mount. Held in an object so the cleanup can read the
	// latest count without touching the ref itself.
	const listenerGeneration = useRef({ n: 0 });
	// Failures already counted in this visit, by result identity. A second
	// results view and a remount share the atom value, so they must not count
	// again. A new visit replaces the set.
	const reportedQueryErrors = useRef(new WeakSet<object>());
	// Successful results already applied in this visit. Two results views share
	// the atom value, so the second must not treat the same payload as a new
	// recovery. A new visit replaces the set.
	const reportedSuccesses = useRef(new WeakSet<object>());
	// Empty Cypher results already counted in this visit. A second results pane
	// shares the atom value, so it must not count the same payload again.
	const reportedEmptyResults = useRef(new WeakSet<object>());
	// Visible time at which the latest query failure became unresolved. Null
	// once a later result succeeds, or when the visit has not failed a query.
	// Schema failures do not set this. A new failure moves it forward and
	// allows one more Cypher reattempt.
	const unresolvedFailureAt = useRef<number | null>(null);
	const reattemptedSinceFailure = useRef(false);
	const leftWithoutSuccessFired = useRef(false);
	// True from a query run until a result click, the next run, or the page
	// going away. A hidden tab leaves it set, so returning and clicking still
	// counts as a click.
	const queryAwaitingClick = useRef(false);
	// Assigned by the page-lifecycle effect so a later query run can close the
	// previous one on the same path as pagehide and unmount.
	const closeQueryWithoutClickRef = useRef<() => void>(() => {});
	// Last results view already counted for a result in this visit. A second
	// pane reporting the same view must not count again. Switching view, or a
	// new visit, counts again.
	const reportedResultsViews = useRef(new WeakMap<object, LabsResultsViewMode>());
	// Guards against StrictMode's intentional double-invocation of effects
	// in development (mount → cleanup → re-mount), which would otherwise
	// produce two `screen.twgLabs.viewed` events per real mount. Scoped to
	// the mount page view only — the back/forward-cache restore below fires its
	// own page view deliberately.
	const screenViewedFired = useRef(false);

	useEffect(() => {
		if (screenViewedFired.current) {
			return;
		}
		screenViewedFired.current = true;
		fireEvent(`screen.${surfaceRef.current}.viewed`, { visitId: visitId.current });
		if (openedFromShareableLink(surfaceRef.current)) {
			fireEvent(`track.${surfaceRef.current}.linkOpened`, { visitId: visitId.current });
		}
		// `fireEvent` is deliberately omitted from the dep array — but NOT
		// because it is referentially stable. `useAnalyticsEvents` memoises it
		// with `useCallback(..., [createAnalyticsEvent])`, so its identity
		// changes whenever `createAnalyticsEvent` does. Omitting it is safe
		// here because the `screenViewedFired` ref guard above already makes
		// any re-run a no-op, so the screen event can only ever fire once per
		// mount regardless of how often `fireEvent` is re-created.
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	useEffect(() => {
		const doc = getDocument();
		if (!doc || typeof window === 'undefined') {
			return undefined;
		}
		const generations = listenerGeneration.current;
		const generation = ++generations.n;

		/**
		 * Reports that the user left without a meaningful interaction. Idempotent
		 * for the visit: a hidden signal and a later unmount, or visibilitychange
		 * together with pagehide, must not emit two events.
		 */
		const reportAbandoned = (reason: LabsAbandonReason): void => {
			if (abandonedFired.current || firstInteractionFired.current) {
				return;
			}
			abandonedFired.current = true;
			const openSegmentMs = segmentOpen.current ? Math.max(0, now() - segmentStart.current) : 0;
			fireEventRef.current(`track.${surfaceRef.current}.abandoned`, {
				timeOnPageMs: Math.max(0, Math.round(accruedMs.current + openSegmentMs)),
				reason,
				visitId: visitId.current,
			});
		};

		/**
		 * Reports that the user left with a query failure still unresolved.
		 * Idempotent for the visit, and independent of whether they had already
		 * interacted: running the failed query is itself an interaction, so the
		 * abandon event above will usually have stayed silent.
		 */
		const reportLeftWithoutSuccess = (reason: LabsAbandonReason): void => {
			if (leftWithoutSuccessFired.current || unresolvedFailureAt.current == null) {
				return;
			}
			leftWithoutSuccessFired.current = true;
			const openSegmentMs = segmentOpen.current ? Math.max(0, now() - segmentStart.current) : 0;
			fireEventRef.current(`track.${surfaceRef.current}.leftWithoutSuccess`, {
				durationSinceFailureMs: Math.max(
					0,
					Math.round(accruedMs.current + openSegmentMs - unresolvedFailureAt.current),
				),
				reason,
				visitId: visitId.current,
			});
		};

		/**
		 * Closes the open query run as unclicked. A result click, the next run,
		 * pagehide, unmount, and a back/forward-cache restore can all arrive,
		 * and only the first one counts. Hiding the tab does not call this.
		 */
		const closeQueryWithoutClick = (): void => {
			if (!queryAwaitingClick.current) {
				return;
			}
			queryAwaitingClick.current = false;
			fireEventRef.current(`track.${surfaceRef.current}.queryUnclicked`, {
				visitId: visitId.current,
			});
		};
		closeQueryWithoutClickRef.current = closeQueryWithoutClick;

		// A page can mount while already hidden (prerender, or opened in a
		// background tab). There is no visible time to measure yet, so leave
		// the segment closed until the first transition to visible.
		if (doc.visibilityState === 'hidden') {
			segmentOpen.current = false;
		}

		/**
		 * Reports the time accrued since the page last became visible, then
		 * closes the segment. Idempotent: hidden is signalled by both
		 * `visibilitychange` and `pagehide`, and browsers differ over which
		 * (or both) they emit, so the closed-segment check is what prevents a
		 * single background event being counted twice.
		 */
		const endSegment = (): void => {
			if (!segmentOpen.current) {
				return;
			}
			segmentOpen.current = false;
			// Rounded because the registry has no float type, and
			// sub-millisecond precision is meaningless for time on page.
			const durationMs = Math.round(now() - segmentStart.current);
			accruedMs.current += durationMs;
			fireEventRef.current(`track.session.ended.${surfaceRef.current}`, {
				durationMs,
				visitId: visitId.current,
			});
			// The segment is already closed, so this reads the visit's visible time
			// including the segment just reported, and does not add it twice.
			reportAbandoned('pageHidden');
			reportLeftWithoutSuccess('pageHidden');
		};

		/**
		 * Begins measuring a new segment. Idempotent so that a redundant
		 * visible signal cannot silently discard time already accrued by
		 * resetting the clock mid-segment.
		 */
		const startSegment = (): void => {
			if (segmentOpen.current) {
				return;
			}
			segmentStart.current = now();
			segmentOpen.current = true;
		};

		const onVisibilityChange = (): void => {
			if (doc.visibilityState === 'hidden') {
				endSegment();
			} else {
				startSegment();
			}
		};

		// BFCache restore: when the user navigates away with `pagehide` and
		// later returns via the browser's back/forward cache, the JS context is
		// reused — including every ref here. `pageshow` with
		// `event.persisted === true` signals such a restore. We treat it as a
		// new visit: a fresh `visitId`, a matching page view so visit counts
		// and duration records stay aligned, and a clock restarted from now so
		// time spent in BFCache is not billed to the restored visit.
		const onPageShow = (event: Event): void => {
			if (!(event as PageTransitionEvent).persisted) {
				return;
			}
			// pagehide normally reports the abandon before we get here. If it did not,
			// the previous visit still went away with no interaction.
			reportAbandoned('pageHidden');
			reportLeftWithoutSuccess('pageHidden');
			// The previous visit is over. Close an unclicked query under the
			// visitId it belonged to, before that id is replaced.
			closeQueryWithoutClick();
			// Close (without reporting) any segment left open by an out-of-order
			// visible signal, so `startSegment` below is guaranteed to re-base
			// the clock to the moment of restore.
			segmentOpen.current = false;
			accruedMs.current = 0;
			firstInteractionFired.current = false;
			cyphieUsedFired.current = false;
			cyphieQueryAwaitingFirstRun.current = false;
			abandonedFired.current = false;
			reportedQueryErrors.current = new WeakSet();
			reportedSuccesses.current = new WeakSet();
			reportedEmptyResults.current = new WeakSet();
			unresolvedFailureAt.current = null;
			reattemptedSinceFailure.current = false;
			leftWithoutSuccessFired.current = false;
			queryAwaitingClick.current = false;
			queryStartedAt.current = null;
			reportedResultsViews.current = new WeakMap();
			visitId.current = createVisitId();
			fireEventRef.current(`screen.${surfaceRef.current}.viewed`, { visitId: visitId.current });
			if (openedFromShareableLink(surfaceRef.current)) {
				fireEventRef.current(`track.${surfaceRef.current}.linkOpened`, {
					visitId: visitId.current,
				});
			}
			// The mode and its callback are unchanged, so useTrackLabsColorMode
			// does not rerun. Record the mode still on screen for this visit.
			const colorMode = lastReportedColorMode.current;
			if (colorMode) {
				fireEventRef.current('track.twgLabs.colorModeUsed', {
					colorMode,
					visitId: visitId.current,
				});
			}
			startSegment();
		};

		const onPageHide = (): void => {
			endSegment();
			closeQueryWithoutClick();
		};

		doc.addEventListener('visibilitychange', onVisibilityChange);
		window.addEventListener('pagehide', onPageHide);
		window.addEventListener('pageshow', onPageShow);

		return () => {
			doc.removeEventListener('visibilitychange', onVisibilityChange);
			window.removeEventListener('pagehide', onPageHide);
			window.removeEventListener('pageshow', onPageShow);
			// Deferred so StrictMode's cleanup → re-run does not count as leaving.
			// A real unmount leaves the generation count unchanged, and in-app
			// navigation does not emit pagehide, so this is the unmounted signal.
			queueMicrotask(() => {
				if (generations.n !== generation) {
					return;
				}
				reportAbandoned('unmounted');
				reportLeftWithoutSuccess('unmounted');
				closeQueryWithoutClick();
				endSegment();
			});
		};
		// Bound exactly once per mount. Every value the listeners touch is
		// either module scope or a ref — including `fireEvent`, via
		// `fireEventRef` — so this dependency list is genuinely exhaustive and
		// re-binding is never required.
	}, []);

	const reportLabsError = useCallback<ReportLabsError>((errorType, failure) => {
		if (failure) {
			if (reportedQueryErrors.current.has(failure)) {
				return;
			}
			reportedQueryErrors.current.add(failure);
		}
		fireEventRef.current(`track.${surfaceRef.current}.errored`, {
			errorType,
			visitId: visitId.current,
		});
		// A schema load failure is not a Cypher query the user can rewrite.
		if (errorType === LabsErrorType.Schema) {
			return;
		}
		if (cyphieQueryAwaitingFirstRun.current) {
			cyphieQueryAwaitingFirstRun.current = false;
			fireEventRef.current('track.twgLabs.cyphieQueryFirstRun', {
				outcome: LabsCyphieQueryFirstRun.Error,
				visitId: visitId.current,
			});
		}
		if (queryStartedAt.current !== null) {
			fireEventRef.current('track.queryBuilder.queryFinished', {
				visitId: visitId.current,
				status: 'error',
				durationMs: Math.max(0, Math.round(now() - queryStartedAt.current)),
			});
			queryStartedAt.current = null;
		}

		const openSegmentMs = segmentOpen.current ? Math.max(0, now() - segmentStart.current) : 0;
		unresolvedFailureAt.current = accruedMs.current + openSegmentMs;
		reattemptedSinceFailure.current = false;
	}, []);

	const reportLabsQuerySuccess = useCallback<ReportLabsQuerySuccess>((result) => {
		if (result) {
			if (reportedSuccesses.current.has(result)) {
				return;
			}
			reportedSuccesses.current.add(result);
		}

		if (cyphieQueryAwaitingFirstRun.current) {
			cyphieQueryAwaitingFirstRun.current = false;
			fireEventRef.current('track.twgLabs.cyphieQueryFirstRun', {
				outcome: LabsCyphieQueryFirstRun.Success,
				visitId: visitId.current,
			});
		}

		if (queryStartedAt.current !== null) {
			fireEventRef.current('track.queryBuilder.queryFinished', {
				visitId: visitId.current,
				status: 'success',
				durationMs: Math.max(0, Math.round(now() - queryStartedAt.current)),
			});
			queryStartedAt.current = null;
		}
		unresolvedFailureAt.current = null;
		reattemptedSinceFailure.current = false;
	}, []);

	const reportLabsCypherReattempt = useCallback<ReportLabsCypherReattempt>(() => {
		if (unresolvedFailureAt.current == null || reattemptedSinceFailure.current) {
			return;
		}
		reattemptedSinceFailure.current = true;
		const openSegmentMs = segmentOpen.current ? Math.max(0, now() - segmentStart.current) : 0;
		fireEventRef.current(`track.${surfaceRef.current}.reattempted`, {
			durationSinceFailureMs: Math.max(
				0,
				Math.round(accruedMs.current + openSegmentMs - unresolvedFailureAt.current),
			),
			visitId: visitId.current,
		});
	}, []);

	const reportLabsQueryRun = useCallback<ReportLabsQueryRun>(() => {
		closeQueryWithoutClickRef.current();
		queryAwaitingClick.current = true;
		if (surfaceRef.current === 'queryBuilder') {
			queryStartedAt.current = now();
			fireEventRef.current('track.queryBuilder.queryExecuted', { visitId: visitId.current });
			return;
		}
		fireEventRef.current('track.twgLabs.queryExecuted', { visitId: visitId.current });
	}, []);

	const reportLabsResultEngaged = useCallback<ReportLabsResultEngaged>(() => {
		queryAwaitingClick.current = false;
	}, []);

	const reportLabsLinkCopied = useCallback<ReportLabsLinkCopied>(() => {
		fireEventRef.current(`track.${surfaceRef.current}.linkCopied`, { visitId: visitId.current });
	}, []);

	const reportLabsResultClick = useCallback<ReportLabsResultClick>((clickTarget) => {
		fireEventRef.current(`track.${surfaceRef.current}.resultClicked`, {
			clickTarget,
			visitId: visitId.current,
		});
	}, []);

	const reportLabsHelperClick = useCallback<ReportLabsHelperClick>((helper) => {
		fireEventRef.current(`track.${surfaceRef.current}.helperClicked`, {
			helper,
			visitId: visitId.current,
		});
	}, []);

	const reportLabsCyphieFeedback = useCallback<ReportLabsCyphieFeedback>((feedback) => {
		fireEventRef.current('track.twgLabs.cyphieFeedbackClicked', {
			feedback,
			visitId: visitId.current,
		});
	}, []);

	const reportLabsCyphieMessage = useCallback<ReportLabsCyphieMessage>((mode) => {
		if (!cyphieUsedFired.current) {
			cyphieUsedFired.current = true;
			fireEventRef.current('track.twgLabs.cyphieUsed', { visitId: visitId.current });
		}
		fireEventRef.current('track.twgLabs.cyphieMessaged', {
			mode,
			visitId: visitId.current,
		});
	}, []);

	const reportLabsCyphieQuerySelected = useCallback<ReportLabsCyphieQuerySelected>(() => {
		cyphieQueryAwaitingFirstRun.current = true;
		fireEventRef.current('track.twgLabs.cyphieQuerySelected', { visitId: visitId.current });
	}, []);

	const reportLabsColorMode = useCallback<ReportLabsColorMode>((colorMode) => {
		if (lastReportedColorMode.current === colorMode) {
			return;
		}
		lastReportedColorMode.current = colorMode;
		fireEventRef.current('track.twgLabs.colorModeUsed', {
			colorMode,
			visitId: visitId.current,
		});
	}, []);

	const reportLabsMoreInfoClick = useCallback<ReportLabsMoreInfoClick>((link) => {
		// Query Builder mounts this tracker with the shared sidebar. Those clicks
		// belong to a different screen, so they stay off the Labs visit metric.
		if (surfaceRef.current !== 'twgLabs') {
			return;
		}
		fireEventRef.current('track.twgLabs.moreInfoClicked', {
			link,
			visitId: visitId.current,
		});
	}, []);

	const readLabsVisitId = useCallback<ReadLabsVisitId>(() => visitId.current, []);

	const reportLabsEmptyResult = useCallback<ReportLabsEmptyResult>((result) => {
		if (reportedEmptyResults.current.has(result)) {
			return;
		}
		reportedEmptyResults.current.add(result);
		fireEventRef.current(`track.${surfaceRef.current}.emptyResult`, { visitId: visitId.current });
	}, []);

	const reportLabsResultsView = useCallback<ReportLabsResultsView>((view, result) => {
		if (result) {
			if (reportedResultsViews.current.get(result) === view) {
				return;
			}
			reportedResultsViews.current.set(result, view);
		}
		fireEventRef.current(`track.${surfaceRef.current}.resultsViewUsed`, {
			view,
			visitId: visitId.current,
		});
	}, []);

	useEffect(() => {
		if (surface !== 'twgLabs') {
			return;
		}
		labsCyphiePortal.feedback = reportLabsCyphieFeedback;
		labsCyphiePortal.message = reportLabsCyphieMessage;
		labsCyphiePortal.querySelected = reportLabsCyphieQuerySelected;
		return () => {
			labsCyphiePortal.feedback = noopReportLabsCyphieFeedback;
			labsCyphiePortal.message = noopReportLabsCyphieMessage;
			labsCyphiePortal.querySelected = noopReportLabsCyphieQuerySelected;
		};
	}, [surface, reportLabsCyphieFeedback, reportLabsCyphieMessage, reportLabsCyphieQuerySelected]);

	const reportFirstInteraction = useCallback<ReportLabsFirstInteraction>((interactionType) => {
		if (firstInteractionFired.current) {
			return;
		}
		firstInteractionFired.current = true;
		const openSegmentMs = segmentOpen.current ? now() - segmentStart.current : 0;
		fireEventRef.current(`track.${surfaceRef.current}.firstInteracted`, {
			timeToFirstInteractionMs: Math.max(0, Math.round(accruedMs.current + openSegmentMs)),
			interactionType,
			visitId: visitId.current,
		});
	}, []);

	return (
		<LabsErrorContext.Provider value={reportLabsError}>
			<LabsQuerySuccessContext.Provider value={reportLabsQuerySuccess}>
				<LabsCypherReattemptContext.Provider value={reportLabsCypherReattempt}>
					<LabsResultClickContext.Provider value={reportLabsResultClick}>
						<LabsResultsViewContext.Provider value={reportLabsResultsView}>
							<LabsFirstInteractionContext.Provider value={reportFirstInteraction}>
								<LabsQueryRunContext.Provider value={reportLabsQueryRun}>
									<LabsResultEngagedContext.Provider value={reportLabsResultEngaged}>
										<LabsLinkCopiedContext.Provider value={reportLabsLinkCopied}>
											<LabsHelperClickContext.Provider value={reportLabsHelperClick}>
												<LabsVisitIdContext.Provider value={readLabsVisitId}>
													<LabsEmptyResultContext.Provider value={reportLabsEmptyResult}>
														<LabsCyphieFeedbackContext.Provider value={reportLabsCyphieFeedback}>
															<LabsCyphieMessageContext.Provider value={reportLabsCyphieMessage}>
																<LabsCyphieQuerySelectedContext.Provider
																	value={reportLabsCyphieQuerySelected}
																>
																	<LabsColorModeContext.Provider value={reportLabsColorMode}>
																		<LabsMoreInfoClickContext.Provider
																			value={reportLabsMoreInfoClick}
																		>
																			{children}
																		</LabsMoreInfoClickContext.Provider>
																	</LabsColorModeContext.Provider>
																</LabsCyphieQuerySelectedContext.Provider>
															</LabsCyphieMessageContext.Provider>
														</LabsCyphieFeedbackContext.Provider>
													</LabsEmptyResultContext.Provider>
												</LabsVisitIdContext.Provider>
											</LabsHelperClickContext.Provider>
										</LabsLinkCopiedContext.Provider>
									</LabsResultEngagedContext.Provider>
								</LabsQueryRunContext.Provider>
							</LabsFirstInteractionContext.Provider>
						</LabsResultsViewContext.Provider>
					</LabsResultClickContext.Provider>
				</LabsCypherReattemptContext.Provider>
			</LabsQuerySuccessContext.Provider>
		</LabsErrorContext.Provider>
	);
};

/**
 * Returns a stable callback for reporting the first meaningful interaction
 * in the current Labs visit.
 *
 * When no `LabsSessionTracker` is mounted this is a no-op rather than a
 * throw. Call sites are ordinary workbench controls, and they are rendered
 * in tests and Storybook without the analytics tree.
 */
export const useReportLabsFirstInteraction = (): ReportLabsFirstInteraction =>
	useContext(LabsFirstInteractionContext);

/**
 * Returns a stable callback for reporting an error state in the current Labs
 * visit. When no `LabsSessionTracker` is mounted this is a no-op rather than
 * a throw, so Query Builder and Storybook can render the shared results view.
 */
export const useReportLabsError = (): ReportLabsError => useContext(LabsErrorContext);

/**
 * Returns a stable callback for the first Cypher edit after a query failure.
 * When no `LabsSessionTracker` is mounted this is a no-op rather than a throw.
 */
export const useReportLabsCypherReattempt = (): ReportLabsCypherReattempt =>
	useContext(LabsCypherReattemptContext);

/**
 * Returns a stable callback for a query result that has no error. When no
 * `LabsSessionTracker` is mounted this is a no-op rather than a throw.
 */
export const useReportLabsQuerySuccess = (): ReportLabsQuerySuccess =>
	useContext(LabsQuerySuccessContext);

/**
 * Returns a stable callback for a Labs editor query run. When no
 * `LabsSessionTracker` is mounted this is a no-op rather than a throw.
 */
export const useReportLabsQueryRun = (): ReportLabsQueryRun => useContext(LabsQueryRunContext);

/**
 * Returns a stable callback for a results row or smart-link click. When no
 * `LabsSessionTracker` is mounted this is a no-op rather than a throw.
 */
export const useReportLabsResultEngaged = (): ReportLabsResultEngaged =>
	useContext(LabsResultEngagedContext);

/**
 * Returns a stable callback for a paperclip click. When no
 * `LabsSessionTracker` is mounted this is a no-op rather than a throw.
 */
export const useReportLabsLinkCopied = (): ReportLabsLinkCopied =>
	useContext(LabsLinkCopiedContext);

/**
 * Returns a stable callback for a smart-link or results-row click. Each call
 * fires once. When no `LabsSessionTracker` is mounted this is a no-op.
 */
export const useReportLabsResultClick = (): ReportLabsResultClick =>
	useContext(LabsResultClickContext);

/**
 * Returns a stable callback for the results view on screen. When no
 * `LabsSessionTracker` is mounted this is a no-op.
 */
export const useReportLabsResultsView = (): ReportLabsResultsView =>
	useContext(LabsResultsViewContext);

/**
 * Returns a stable callback for a results-box helper click. Each call fires
 * once. When no `LabsSessionTracker` is mounted this is a no-op.
 */
export const useReportLabsHelperClick = (): ReportLabsHelperClick =>
	useContext(LabsHelperClickContext);

/**
 * Returns a stable callback for a Cyphie thumbs or reason click. Each call
 * fires once. The chat panel renders outside this provider, so a click there
 * uses the reporters the Labs tracker published. When no `LabsSessionTracker`
 * is mounted this is a no-op.
 */
export const useReportLabsCyphieFeedback = (): ReportLabsCyphieFeedback => {
	const fromContext = useContext(LabsCyphieFeedbackContext);
	return useCallback(
		(feedback) => {
			if (fromContext !== noopReportLabsCyphieFeedback) {
				fromContext(feedback);
				return;
			}
			labsCyphiePortal.feedback(feedback);
		},
		[fromContext],
	);
};

/**
 * Returns a stable callback for a message sent to Cyphie. The first call in a
 * visit also records that Cyphie was used. The chat panel renders outside this
 * provider, so a send there uses the reporters the Labs tracker published.
 * When no `LabsSessionTracker` is mounted this is a no-op.
 */
export const useReportLabsCyphieMessage = (): ReportLabsCyphieMessage => {
	const fromContext = useContext(LabsCyphieMessageContext);
	return useCallback(
		(mode) => {
			if (fromContext !== noopReportLabsCyphieMessage) {
				fromContext(mode);
				return;
			}
			labsCyphiePortal.message(mode);
		},
		[fromContext],
	);
};

/**
 * Returns a stable callback for Use in Editor on a Cyphie query. The next
 * query run is recorded as that query's first try. The chat panel renders
 * outside this provider, so that click uses the reporters the Labs tracker
 * published. When no `LabsSessionTracker` is mounted this is a no-op.
 */
export const useReportLabsCyphieQuerySelected = (): ReportLabsCyphieQuerySelected => {
	const fromContext = useContext(LabsCyphieQuerySelectedContext);
	return useCallback(() => {
		if (fromContext !== noopReportLabsCyphieQuerySelected) {
			fromContext();
			return;
		}
		labsCyphiePortal.querySelected();
	}, [fromContext]);
};

/**
 * Returns a stable callback for the color mode on screen. When no
 * `LabsSessionTracker` is mounted this is a no-op.
 */
export const useReportLabsColorMode = (): ReportLabsColorMode => useContext(LabsColorModeContext);

/**
 * Records the Labs color mode while it is on screen. The same mode in a row
 * is recorded once. A back/forward-cache restore records that mode again from
 * the tracker, because this effect does not rerun when the mode is unchanged.
 * When no `LabsSessionTracker` is mounted this is a no-op.
 */
export const useTrackLabsColorMode = (colorMode: LabsAppearanceMode): void => {
	const reportLabsColorMode = useReportLabsColorMode();
	useEffect(() => {
		reportLabsColorMode(colorMode);
	}, [colorMode, reportLabsColorMode]);
};

/**
 * Returns a stable callback for a more-info link click. Each call fires once
 * on TWG Labs. When no `LabsSessionTracker` is mounted this is a no-op.
 */
export const useReportLabsMoreInfoClick = (): ReportLabsMoreInfoClick =>
	useContext(LabsMoreInfoClickContext);

/**
 * Returns the current Labs visit id. Empty when no `LabsSessionTracker` is
 * mounted.
 */
export const useLabsVisitId = (): ReadLabsVisitId => useContext(LabsVisitIdContext);

/**
 * Returns a stable callback for a successful Cypher result with no rows. The
 * same result is recorded once per visit. When no `LabsSessionTracker` is
 * mounted this is a no-op.
 */
export const useReportLabsEmptyResult = (): ReportLabsEmptyResult =>
	useContext(LabsEmptyResultContext);
